// The recent list (notifications overhaul, slice 21). `GET
// /v1/me/notification-deliveries` is a keyset page of the caller's own email
// rows from the last 7 days, each with a closed `why` code the server derived
// from stored facts. This module owns the read, the grouping by tier, and the
// ONE map from a `why` code to the sentence the page shows.
//
// The row is the domain's published resource (`NotificationDeliveryWire`), parsed
// rather than cast, so a field that vanished is this list's own error state and
// not a blank cell.

import {
  NOTIFICATION_TIERS,
  type NotificationDeliveryWhy,
  type NotificationDeliveryWire,
  NotificationDeliveryWireSchema,
  NotificationTier,
} from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch, withCursor } from './api';
import { pageSchema } from './paged-schema';
import type { Page } from './paged-types';
import { SESSION_TOKEN } from './session';

/** One recent email row. `Readonly` because this app treats a response as immutable. */
export type NotificationDelivery = Readonly<NotificationDeliveryWire>;

const notificationDeliveriesPageSchema = pageSchema(NotificationDeliveryWireSchema);

export const NOTIFICATION_RECENT_QUERY_KEY = 'notification_recent';

/**
 * One page of the caller's recent email rows, newest first. Idle with no
 * session. Hand it to `usePagedQuery`: the cursor accessor is part of the key.
 */
export function useNotificationRecent(
  cursor: () => string | null,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<Page<NotificationDelivery>>(() => ({
    queryKey: [NOTIFICATION_RECENT_QUERY_KEY, cursor(), token, activeAccountId()],
    enabled: token !== null,
    queryFn: () =>
      apiFetch(withCursor('/v1/me/notification-deliveries', cursor()), token, undefined, {
        schema: notificationDeliveriesPageSchema,
      }),
  }));
}

/** A tier the page shows. The hidden tier never reaches a customer's list. */
export type ShownTier = Exclude<NotificationTier, 'hidden'>;

export type DeliveryGroup = {
  readonly tier: ShownTier;
  readonly rows: readonly NotificationDelivery[];
};

/**
 * Rows grouped by tier, in the domain's tier order. Each group keeps the
 * server's order (newest first); a tier with no rows is dropped.
 */
export function groupDeliveriesByTier(rows: readonly NotificationDelivery[]): DeliveryGroup[] {
  return NOTIFICATION_TIERS.filter((tier): tier is ShownTier => tier !== NotificationTier.Hidden)
    .map((tier) => ({ tier, rows: rows.filter((row) => row.tier === tier) }))
    .filter((group) => group.rows.length > 0);
}

/**
 * `why` -> the sentence. CLOSED: a code added to the domain without a sentence
 * here is a type error, not a `⟦key⟧` on a customer's screen.
 *
 * `why_turned_off` must never say the person turned anything off: the code is
 * every suppressed row not told earlier, whatever suppressed it (`deliveryWhy`,
 * `@developerz/domain`), so its sentence names a setting or a rule as the
 * holder-back and the locale strings are pinned to that wording.
 */
export const DELIVERY_WHY_KEY: Readonly<Record<NotificationDeliveryWhy, string>> = {
  your_setting: 'notifications_settings.recent.why_your_setting',
  always_on: 'notifications_settings.recent.why_always_on',
  batched: 'notifications_settings.recent.why_batched',
  already_told: 'notifications_settings.recent.why_already_told',
  turned_off: 'notifications_settings.recent.why_turned_off',
  nowhere_to_send: 'notifications_settings.recent.why_nowhere_to_send',
  failed: 'notifications_settings.recent.why_failed',
};

/**
 * When the email for this row went out, or `null` if it has not. A row that
 * went out inside a summary may carry the time on the row, on its bundle, or on
 * both, so the bundle's time is the fallback.
 */
export function deliverySentAt(
  row: Pick<NotificationDelivery, 'sent_at' | 'bundle'>,
): string | null {
  return row.sent_at ?? row.bundle?.sent_at ?? null;
}

/**
 * The key for one row's reason. `batched` covers three stored facts and the
 * sentence must not lie about any of them: still waiting for its summary
 * (nothing sent yet), sent in a daily summary, sent in an hourly one.
 */
export function deliveryWhyKey(
  row: Pick<NotificationDelivery, 'why' | 'sent_at' | 'bundle'>,
): string {
  if (row.why === 'batched' && deliverySentAt(row) === null) {
    return 'notifications_settings.recent.why_batched_waiting';
  }
  if (row.why === 'batched' && row.bundle?.cadence === 'daily') {
    return 'notifications_settings.recent.why_batched_daily';
  }
  return DELIVERY_WHY_KEY[row.why];
}
